import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import {
  useUser,
  useUserFinancials,
  useUserSubscription,
  useGrantPlus,
  useExtendTrial,
  useSetQuotaLimit,
  useResetQuotaUsage,
  useDeleteOverride,
  useUpdateUser,
  useDeleteUser,
} from '@/features/users/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';
import { Segmented } from '@/components/ui/Segmented';
import { Select } from '@/components/ui/Select';
import { useEnabledLocales } from '@/features/i18n/api';
import { useRouteContext } from '@tanstack/react-router';
import { useUserEmails, useSendEmail, useEmailTemplates } from '@/features/emails/api';
import { useSuppressionForEmail, useClearSuppression } from '@/features/email-suppressions/api';
import type {
  AdminDeviceSessionRow,
  AdminUserFinancialsBucket,
  AdminEntitlementOverrideRow,
  AdminSubscriptionEventRow,
  AdminSubscriptionRow,
  EmailLogRow,
} from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/users/$id')({
  component: UserDetailPage,
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 10);
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

// ── Entitlement card ──────────────────────────────────────────────────────────

function EntitlementCard({
  entitlement,
  carriedOver,
}: {
  entitlement: { plan: string; trial: { active: boolean; endsAt: string | null } | null; quota: { used: number; limit: number; resetsAt: string } };
  carriedOver: { used: number; appliedAt: string } | null;
}) {
  const { plan, trial, quota } = entitlement;
  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
      <h2 className="font-serif text-xl">Entitlement</h2>
      {carriedOver && (
        <p
          className="rounded-md bg-honey-tint px-3 py-2 text-sm text-ink-soft"
          data-testid="entitlement-carryover-note"
        >
          <strong>{carriedOver.used}</strong> of this period&rsquo;s quota was
          carried over from a previously deleted account with the same Apple/Google
          sign-in (applied {formatDate(carriedOver.appliedAt)}). This prevents
          resetting the monthly allowance by deleting and re-creating an account —
          it clears at the next period reset.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Plan">
          <Badge tone={plan === 'plus' ? 'honey' : 'neutral'}>
            {plan === 'plus' ? 'Plus' : 'Free'}
          </Badge>
        </Stat>
        <Stat label="Trial">
          {trial?.active ? (
            <span className="text-sm">
              Active until {trial.endsAt ? formatDate(trial.endsAt) : '?'}
            </span>
          ) : (
            <span className="text-sm text-ink-faint">None</span>
          )}
        </Stat>
        <Stat label="Quota used">
          <span className="font-mono text-sm tabular-nums">
            {quota.used} / {quota.limit}
          </span>
        </Stat>
        <Stat label="Period resets">
          <span className="font-mono text-sm">{formatDate(quota.resetsAt)}</span>
        </Stat>
      </div>
    </Card>
  );
}

// ── Onboarding card ───────────────────────────────────────────────────────────

function OnboardingCard({
  userId,
  onboardingCompletedAt,
}: {
  userId: string;
  onboardingCompletedAt: string | null;
}) {
  const update = useUpdateUser();
  const [error, setError] = useState<string | null>(null);
  const done = Boolean(onboardingCompletedAt);

  const handleToggle = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: userId, body: { onboarded: !done } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-3" data-testid="user-onboarding-card">
      <h2 className="font-serif text-xl">Onboarding</h2>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1" data-testid="user-onboarding-status">
          <div>
            {done ? (
              <Badge tone="sage">Completed</Badge>
            ) : (
              <Badge tone="honey">Not completed</Badge>
            )}
          </div>
          <span className="text-xs text-ink-soft">
            {done
              ? `Completed ${formatDateTime(onboardingCompletedAt)}`
              : 'The app forces the first-run flow on next launch; the first generation is free.'}
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          data-testid="user-onboarding-toggle"
          disabled={update.isPending}
          onClick={handleToggle}
        >
          {update.isPending
            ? 'Saving…'
            : done
              ? 'Reset onboarding'
              : 'Mark completed'}
        </Button>
      </div>
      {error && <ErrorBox message={error} />}
    </Card>
  );
}

// ── Subscription card ─────────────────────────────────────────────────────────

function SubscriptionCard({ subscription }: { subscription: AdminSubscriptionRow | null }) {
  if (!subscription) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2">
        <h2 className="font-serif text-xl">Subscription</h2>
        <p className="text-ink-faint text-sm">No active subscription row.</p>
      </Card>
    );
  }
  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
      <h2 className="font-serif text-xl">Subscription</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Platform">
          <Badge tone="neutral">{subscription.platform}</Badge>
        </Stat>
        <Stat label="Product ID">
          <span className="font-mono text-xs break-all">{subscription.productId}</span>
        </Stat>
        <Stat label="State">
          <Badge tone={subscription.state === 'active' || subscription.state === 'trialing' ? 'sage' : 'neutral'}>
            {subscription.state}
          </Badge>
        </Stat>
        <Stat label="Expires">
          <span className="font-mono text-sm">{formatDate(subscription.expiresAt)}</span>
        </Stat>
        <Stat label="Auto-renew">
          <Badge tone={subscription.autoRenewEnabled ? 'sage' : 'neutral'}>
            {subscription.autoRenewEnabled ? 'On' : 'Off'}
          </Badge>
        </Stat>
        <Stat label="Environment">
          <Badge tone={subscription.environment === 'sandbox' ? 'honey' : 'neutral'}>
            {subscription.environment}
          </Badge>
        </Stat>
      </div>
    </Card>
  );
}

// ── Financials card ───────────────────────────────────────────────────────────

function formatUsd(n: number): string {
  const abs = Math.abs(n);
  // Sub-dollar amounts (typically AI cost) keep 4dp so fractions of a cent
  // don't render as $0.00; larger amounts read as normal currency.
  const digits = abs > 0 && abs < 1 ? 4 : 2;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(digits)}`;
}

function FinancialsColumn({
  title,
  subtitle,
  bucket,
  testId,
}: {
  title: string;
  subtitle: string;
  bucket: AdminUserFinancialsBucket;
  testId: string;
}) {
  const rows: { label: string; hint: string; value: number; net?: boolean }[] = [
    { label: 'Outgoing', hint: 'AI generation cost', value: bucket.costUsd },
    { label: 'Incoming', hint: 'Subscription revenue', value: bucket.revenueUsd },
    { label: 'Net', hint: 'Incoming − outgoing', value: bucket.netUsd, net: true },
  ];
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs uppercase tracking-wide text-ink-faint">{title}</span>
        <span className="text-xs text-ink-soft">{subtitle}</span>
      </div>
      <div className="flex flex-col">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-baseline justify-between gap-4 py-2 border-b last:border-b-0"
            style={{ borderColor: 'var(--color-hairline)' }}
          >
            <div className="flex flex-col">
              <span className={`text-sm ${r.net ? 'font-medium' : ''}`}>{r.label}</span>
              <span className="text-xs text-ink-faint">{r.hint}</span>
            </div>
            <span
              className={`font-mono text-sm tabular-nums ${
                r.net ? (r.value >= 0 ? 'text-sage-deep font-medium' : 'text-honey-deep font-medium') : ''
              }`}
              data-testid={`${testId}-${r.label.toLowerCase()}`}
            >
              {formatUsd(r.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancialsCard({ userId }: { userId: string }) {
  const { data, isLoading, isError, error } = useUserFinancials(userId);

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4" data-testid="user-financials-card">
      <h2 className="font-serif text-xl">Financials</h2>
      {isLoading && <p className="text-ink-faint text-sm">Loading…</p>}
      {isError && (
        <p className="text-sm text-honey-deep" data-testid="user-financials-error">
          {error instanceof Error ? error.message : 'Failed to load financials.'}
        </p>
      )}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FinancialsColumn
              title="Current period"
              subtitle={`${formatDate(data.periodStart)} → ${formatDate(data.periodEnd)}`}
              bucket={data.currentPeriod}
              testId="user-financials-current"
            />
            <FinancialsColumn
              title="All-time"
              subtitle="Since account creation"
              bucket={data.allTime}
              testId="user-financials-alltime"
            />
          </div>
          <p className="text-xs text-ink-faint">
            All amounts USD. Outgoing sums AI Gateway cost across generation, vision, editing, and
            image stages; incoming sums store prices from recorded subscription webhook events
            (refunds deducted).
          </p>
        </>
      )}
    </Card>
  );
}

// ── Overrides card ────────────────────────────────────────────────────────────

function OverridesCard({
  userId,
  overrides,
}: {
  userId: string;
  overrides: AdminEntitlementOverrideRow[];
}) {
  const deleteOverride = useDeleteOverride(userId);

  if (overrides.length === 0) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2">
        <h2 className="font-serif text-xl">Entitlement overrides</h2>
        <p className="text-ink-faint text-sm">No overrides.</p>
      </Card>
    );
  }

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
      <h2 className="font-serif text-xl">Entitlement overrides</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Quota override</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {overrides.map((o) => (
              <tr
                key={o.id}
                className="border-b last:border-b-0"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <td className="px-3 py-2" data-testid={`override-row-plan-${o.id}`}>
                  <Badge tone={o.plan === 'plus' ? 'honey' : 'neutral'}>
                    {o.plan === 'plus' ? 'Plus' : 'Free'}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs tabular-nums">
                  {o.quotaLimitOverride != null ? o.quotaLimitOverride : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(o.expiresAt)}</td>
                <td className="px-3 py-2 text-ink-soft max-w-xs truncate">{o.reason || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(o.createdAt)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteOverride.mutate(o.id)}
                    disabled={deleteOverride.isPending}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Events card ───────────────────────────────────────────────────────────────

function EventsCard({ events }: { events: AdminSubscriptionEventRow[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (events.length === 0) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2">
        <h2 className="font-serif text-xl">Subscription events</h2>
        <p className="text-ink-faint text-sm">No events.</p>
      </Card>
    );
  }

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
      <h2 className="font-serif text-xl">Subscription events</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Platform</th>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2">Processed</th>
              <th className="px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <Fragment key={e.id}>
                <tr
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-hairline)' }}
                >
                  <td className="px-3 py-2 font-mono text-xs">{e.notificationType}</td>
                  <td className="px-3 py-2">
                    {e.platform ? <Badge tone="neutral">{e.platform}</Badge> : <span className="text-ink-soft">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{formatDateTime(e.receivedAt)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{formatDateTime(e.processedAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                    >
                      {expanded === e.id ? 'Hide' : 'Payload'}
                    </Button>
                  </td>
                </tr>
                {expanded === e.id && (
                  <tr>
                    <td colSpan={5} className="px-3 py-2 bg-cream-deep">
                      <pre
                        className="text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all"
                        style={{ maxHeight: '200px' }}
                      >
                        {(() => {
                          try {
                            return JSON.stringify(JSON.parse(e.rawPayload), null, 2);
                          } catch {
                            return e.rawPayload;
                          }
                        })()}
                      </pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Template IDs that belong to the support inbox (inbound + outbound support sends). */
const SUPPORT_TEMPLATE_IDS = new Set([
  'inbound:support',
  'support_agent_message',
  'support_message_received',
]);

function isSupportRow(row: EmailLogRow): boolean {
  return SUPPORT_TEMPLATE_IDS.has(row.templateId);
}

function EmailHtmlModal({
  open,
  onClose,
  bodyHtml,
}: {
  open: boolean;
  onClose: () => void;
  bodyHtml: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Email preview" size="lg" testId="email-log-view-modal">
      <iframe
        data-testid="email-log-view-iframe"
        sandbox=""
        srcDoc={bodyHtml}
        className="w-full border-0 rounded"
        style={{ height: '60vh', minHeight: '300px' }}
        title="Email HTML preview"
      />
    </Dialog>
  );
}

function EmailLogCard({ userId }: { userId: string }) {
  const { data, isLoading, isError, error } = useUserEmails(userId);
  const sendEmail = useSendEmail(userId);
  const emails: EmailLogRow[] = data?.emails ?? [];
  const [viewHtml, setViewHtml] = useState<string | null>(null);

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4" data-testid="email-log-card">
      <h2 className="font-serif text-xl">Email log</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              <th className="px-3 py-2">Direction</th>
              <th className="px-3 py-2">Template</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Recipient</th>
              <th className="px-3 py-2">Triggered by</th>
              <th className="px-3 py-2">Sent</th>
              <th className="px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-ink-faint text-center">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && isError && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-4 text-center text-honey-deep"
                  data-testid="email-log-error"
                >
                  {error instanceof Error ? error.message : 'Failed to load email log.'}
                </td>
              </tr>
            )}
            {!isLoading && !isError && emails.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-4 text-ink-faint text-center"
                  data-testid="email-log-empty"
                >
                  No emails sent.
                </td>
              </tr>
            )}
            {emails.map((row) => {
              const isIncoming = row.direction === 'incoming';
              const isSupport = isSupportRow(row);
              const isAdminTriggered = row.triggeredByLabel === 'admin';

              return (
                <tr
                  key={row.id}
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-hairline)' }}
                  data-testid={`email-row-${row.id}`}
                  title={row.messageId ?? ''}
                >
                  {/* Direction chip */}
                  <td className="px-3 py-2">
                    <span
                      data-testid={`email-row-direction-${row.id}`}
                      className={`inline-block text-xs rounded px-1.5 py-0.5 font-sans ${
                        isIncoming
                          ? 'bg-sage-light text-sage-deep'
                          : 'bg-cream-deep text-ink-soft'
                      }`}
                    >
                      {isIncoming ? 'Incoming' : 'Outgoing'}
                    </span>
                  </td>
                  {/* Template — empty for incoming rows */}
                  <td className="px-3 py-2 font-mono text-xs" data-testid={`email-row-template-${row.id}`}>
                    {isIncoming ? '' : row.templateId}
                  </td>
                  {/* Status — "received" for incoming rows */}
                  <td className="px-3 py-2" data-testid={`email-row-status-${row.id}`}>
                    {isIncoming ? 'received' : row.status}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{row.recipientEmail}</td>
                  {/* Triggered by — "Customer" for incoming, "Admin" + name for admin-triggered */}
                  <td className="px-3 py-2" data-testid={`email-row-triggered-${row.id}`}>
                    {isIncoming ? (
                      'Customer'
                    ) : isAdminTriggered ? (
                      <div className="flex flex-col gap-0.5">
                        <span>Admin</span>
                        <span
                          className="text-xs text-ink-faint"
                          data-testid={`email-row-triggered-admin-name-${row.id}`}
                        >
                          {row.triggeredByDisplay}
                        </span>
                      </div>
                    ) : (
                      row.triggeredByDisplay
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  {/* Action button: View for support rows, Resend for transactional */}
                  <td className="px-3 py-2 text-right">
                    {isSupport ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`email-row-view-${row.id}`}
                        disabled={!row.bodyHtml}
                        onClick={() => {
                          if (row.bodyHtml) setViewHtml(row.bodyHtml);
                        }}
                      >
                        View
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`email-row-resend-${row.id}`}
                        disabled={sendEmail.isPending}
                        onClick={() =>
                          sendEmail.mutate({ templateId: row.templateId, userId })
                        }
                      >
                        Resend
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {viewHtml !== null && (
        <EmailHtmlModal
          open
          onClose={() => setViewHtml(null)}
          bodyHtml={viewHtml}
        />
      )}
    </Card>
  );
}

function SendEmailDialog({
  userId,
  open,
  onClose,
}: {
  userId: string;
  open: boolean;
  onClose: () => void;
}) {
  const templatesQuery = useEmailTemplates();
  const templates = templatesQuery.data?.templates ?? [];
  const [templateId, setTemplateId] = useState<string>('');
  const sendEmail = useSendEmail(userId);

  // Default to the first template once the registry loads; keep in sync if it
  // changes underneath (rare — generally only on hard refresh after a deploy).
  useEffect(() => {
    if (!templateId && templates.length > 0) {
      setTemplateId(templates[0].id);
    }
  }, [templates, templateId]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!templateId) return;
    sendEmail.mutate(
      { templateId, userId },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  }

  const canSubmit =
    Boolean(templateId) &&
    !sendEmail.isPending &&
    !templatesQuery.isLoading &&
    !templatesQuery.isError;

  return (
    <Dialog open={open} onClose={onClose} title="Send email" testId="send-email-dialog">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-sans">Template</span>
          <select
            data-testid="send-email-template-select"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-paper"
            style={{ borderColor: 'var(--color-hairline)' }}
            disabled={templates.length === 0}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id}
              </option>
            ))}
          </select>
        </label>
        {templatesQuery.isError && (
          <p className="text-sm text-honey-deep" data-testid="send-email-templates-error">
            {templatesQuery.error instanceof Error
              ? templatesQuery.error.message
              : 'Failed to load templates.'}
          </p>
        )}
        {sendEmail.isError && (
          <p className="text-sm text-honey-deep" data-testid="send-email-send-error">
            {sendEmail.error instanceof Error
              ? sendEmail.error.message
              : 'Failed to send email.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            data-testid="send-email-submit"
            disabled={!canSubmit}
          >
            Send
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Action dialogs ────────────────────────────────────────────────────────────

type ActionDialogType = 'grant-plus' | 'extend-trial' | 'quota-limit' | 'reset-usage' | null;

function ActionsRow({
  userId,
  dialogOpen,
  setDialogOpen,
}: {
  userId: string;
  dialogOpen: ActionDialogType;
  setDialogOpen: (t: ActionDialogType) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" size="sm" onClick={() => setDialogOpen('grant-plus')}>
        Grant Plus until…
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setDialogOpen('extend-trial')}>
        Extend trial by N days
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setDialogOpen('quota-limit')}>
        Set quota limit
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setDialogOpen('reset-usage')}>
        Reset quota usage
      </Button>
    </div>
  );
}

function GrantPlusDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const grant = useGrantPlus(userId);
  // Default expiry = 1 month from today
  const defaultExpiry = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    try {
      await grant.mutateAsync({ expiresAt: new Date(expiresAt).toISOString(), reason: reason || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <Dialog open title="Grant Plus" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="Expires on"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
        <Input
          label="Reason (optional)"
          placeholder="e.g. Early supporter comp"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={grant.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={grant.isPending || !expiresAt}>
            {grant.isPending ? 'Granting…' : 'Grant Plus'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ExtendTrialDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const extend = useExtendTrial(userId);
  const [days, setDays] = useState('7');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const n = Number(days);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      setError('Days must be a whole number between 1 and 365.');
      return;
    }
    try {
      await extend.mutateAsync({ days: n, reason: reason || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <Dialog open title="Extend trial" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="Days to extend"
          type="number"
          min={1}
          max={365}
          step={1}
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
        <Input
          label="Reason (optional)"
          placeholder="e.g. Reported bug during trial"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={extend.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={extend.isPending || !days}>
            {extend.isPending ? 'Extending…' : 'Extend trial'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function SetQuotaLimitDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const setLimit = useSetQuotaLimit(userId);
  const [limitInput, setLimitInput] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const n = Number(limitInput);
    if (!Number.isInteger(n) || n < 0 || n > 10000) {
      setError('Limit must be a whole number between 0 and 10,000.');
      return;
    }
    try {
      await setLimit.mutateAsync({ limit: n, reason: reason || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <Dialog open title="Set quota limit" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="New limit"
          type="number"
          min={0}
          max={10000}
          step={1}
          placeholder="e.g. 10"
          value={limitInput}
          onChange={(e) => setLimitInput(e.target.value)}
          hint="Applies for the current billing period."
        />
        <Input
          label="Reason (optional)"
          placeholder="e.g. Power user"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={setLimit.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={setLimit.isPending || limitInput === ''}>
            {setLimit.isPending ? 'Saving…' : 'Set limit'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ResetUsageDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const reset = useResetQuotaUsage(userId);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await reset.mutateAsync();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <Dialog open title="Reset quota usage" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">
          This will set the user's AI generation usage count to <strong>0</strong> for the current
          period. This cannot be undone.
        </p>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={reset.isPending}>
            {reset.isPending ? 'Resetting…' : 'Reset usage'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Edit profile dialog ───────────────────────────────────────────────────────

function EditProfileDialog({
  user,
  onClose,
}: {
  user: { id: string; displayName: string; role: 'user' | 'admin'; locale: string | null };
  onClose: () => void;
}) {
  const { me } = useRouteContext({ from: '/_authed' });
  const update = useUpdateUser();
  const { data: locales } = useEnabledLocales();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [role, setRole] = useState<'user' | 'admin'>(user.role);
  const [locale, setLocale] = useState<string>(user.locale ?? '');
  const [error, setError] = useState<string | null>(null);
  const isSelf = me.id === user.id;

  // If the user's current locale is not in the enabled list (e.g. later
  // disabled), keep it as a selectable option so we don't silently drop it.
  const localeOptions = locales ?? [];
  const currentMissing =
    user.locale && !localeOptions.some((l) => l.tag === user.locale);

  const handleSave = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: user.id,
        body: { displayName, role, locale: locale === '' ? null : locale },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  return (
    <Dialog open title={`Edit ${user.displayName}`} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Role</div>
          <Segmented
            value={role}
            onChange={setRole}
            options={[
              { value: 'user', label: 'User' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
          {isSelf && (
            <div className="text-xs text-ink-faint mt-1">You cannot demote your own account.</div>
          )}
        </div>
        <Select
          label="Locale"
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          data-testid="edit-profile-locale"
        >
          <option value="">None (default en-GB)</option>
          {currentMissing && (
            <option value={user.locale as string}>{user.locale} (disabled)</option>
          )}
          {localeOptions.map((l) => (
            <option key={l.tag} value={l.tag}>
              {l.displayName} ({l.tag})
            </option>
          ))}
        </Select>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={update.isPending || (isSelf && role === 'user')}
            data-testid="edit-profile-save"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

interface DeleteUserDialogProps {
  userId: string;
  displayName: string;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteUserDialog({
  userId,
  displayName,
  onClose,
  onDeleted,
}: DeleteUserDialogProps) {
  const del = useDeleteUser();
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await del.mutateAsync(userId);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <Dialog open testId="delete-user-dialog" title="Delete user" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">
          Are you sure you want to permanently delete <strong>{displayName}</strong>? This action
          cannot be undone.
        </p>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={del.isPending}
            data-testid="delete-user-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={del.isPending}
            data-testid="delete-user-confirm"
          >
            {del.isPending ? 'Deleting…' : 'Delete user'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-ink-faint">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      className="text-xs rounded-lg p-3"
      style={{
        background: 'var(--color-error-bg)',
        border: '1px solid var(--color-error-border)',
        color: 'var(--color-error-text)',
      }}
    >
      {message}
    </div>
  );
}

// ── Suppression panel ─────────────────────────────────────────────────────────

function SuppressionPanel({ userId, userEmail }: { userId: string; userEmail: string }) {
  // Use the per-email endpoint rather than filtering the global list (which is
  // capped at 200 rows): a user whose suppression is outside the top 200
  // would silently show "No active suppression" with the old approach.
  const { data, isLoading, isError, error } = useSuppressionForEmail(userEmail);
  const clearMutation = useClearSuppression(userId);

  if (isLoading) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2">
        <h2 className="font-serif text-xl">Suppression status</h2>
        <p className="text-ink-faint text-sm">Loading…</p>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2">
        <h2 className="font-serif text-xl">Suppression status</h2>
        <p className="text-sm text-red-600" data-testid="user-suppression-error">
          Failed to load suppression status
          {error instanceof Error ? `: ${error.message}` : '.'}
        </p>
      </Card>
    );
  }

  // data is null (or undefined) when no active suppression exists (404 → resolves to null).
  const activeSuppression = data?.suppression ?? null;

  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-3">
      <h2 className="font-serif text-xl">Suppression status</h2>
      {activeSuppression ? (
        <div className="flex items-center justify-between gap-4" data-testid="user-suppression-status">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Badge tone="honey">{activeSuppression.reason}</Badge>
              <span className="font-mono text-xs text-ink-faint">
                {activeSuppression.failureCount} failure{activeSuppression.failureCount !== 1 ? 's' : ''}
              </span>
            </div>
            <span className="text-xs text-ink-soft">
              First failed:{' '}
              {new Date(activeSuppression.firstFailedAt).toISOString().slice(0, 10)}
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            data-testid="user-suppression-clear-button"
            disabled={clearMutation.isPending}
            onClick={() => clearMutation.mutate(userEmail)}
          >
            {clearMutation.isPending ? 'Clearing…' : 'Clear suppression'}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-ink-faint" data-testid="user-suppression-none">
          No active suppression.
        </p>
      )}
    </Card>
  );
}

// ── Profile card ──────────────────────────────────────────────────────────────

function ProfileCard({
  user,
  canDelete,
  onEdit,
  onDelete,
}: {
  user: {
    id: string;
    email: string;
    displayName: string;
    role: 'user' | 'admin';
    locale: string | null;
  };
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { data: locales } = useEnabledLocales();
  const localeLabel = user.locale
    ? (locales?.find((l) => l.tag === user.locale)?.displayName
        ? `${locales.find((l) => l.tag === user.locale)!.displayName} (${user.locale})`
        : user.locale)
    : 'Default (en-GB)';

  return (
    <Card
      shadow="sm"
      className="p-4 sm:p-6 flex flex-col gap-4"
      data-testid="user-profile-card"
    >
      <h2 className="font-serif text-xl">Profile</h2>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-ink-faint">Name</span>
          <span className="text-sm" data-testid="user-profile-name">
            {user.displayName}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-ink-faint">Email</span>
          <span className="text-sm break-all" data-testid="user-profile-email">
            {user.email}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-ink-faint">Role</span>
          <div data-testid="user-profile-role">
            {user.role === 'admin' ? (
              <Badge tone="honey">Admin</Badge>
            ) : (
              <Badge tone="neutral">User</Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-ink-faint">Locale</span>
          <span className="text-sm" data-testid="user-profile-locale">
            {localeLabel}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-ink-faint">User ID</span>
          <span className="font-mono text-xs text-ink-faint break-all" data-testid="user-profile-id">
            {user.id}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: 'var(--color-hairline)' }}>
        <Button
          variant="secondary"
          size="sm"
          data-testid="user-profile-edit-button"
          onClick={onEdit}
        >
          Edit profile
        </Button>
        {canDelete && (
          <Button
            variant="danger"
            size="sm"
            data-testid="user-delete-button"
            onClick={onDelete}
          >
            Delete user
          </Button>
        )}
      </div>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function UserDetailPage() {
  const { id } = Route.useParams();
  const { data: user, isLoading: userLoading } = useUser(id);
  const { data: subData, isLoading: subLoading } = useUserSubscription(id);
  const [dialogOpen, setDialogOpen] = useState<ActionDialogType>(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const { me } = useRouteContext({ from: '/_authed' });
  const navigate = useNavigate();


  if (userLoading || subLoading) {
    return <div className="p-6 text-ink-soft">Loading…</div>;
  }

  if (!user) {
    return <div className="p-6 text-ink-soft">User not found.</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6">
      {/* Header — slim; profile details live in the right-column Profile card */}
      <header data-testid="user-detail-header" className="flex items-center gap-2">
        <Link to="/users" className="text-xs text-ink-faint hover:underline">
          ← Users
        </Link>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start gap-6">
        {/* Main column */}
        <div className="flex flex-col gap-6 min-w-0">
          {/* Device sessions card */}
          <DeviceSessionsCard sessions={user.currentVersions ?? []} />

          {/* Onboarding status + toggle */}
          <OnboardingCard
            userId={user.id}
            onboardingCompletedAt={user.onboardingCompletedAt ?? null}
          />

          {/* Subscription data */}
          {subData ? (
            <>
              <EntitlementCard entitlement={subData.entitlement} carriedOver={subData.carriedOver} />
              <FinancialsCard userId={user.id} />
              <SubscriptionCard subscription={subData.subscription} />

              {/* Actions */}
              <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
                <h2 className="font-serif text-xl">Actions</h2>
                <ActionsRow userId={id} dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} />
                <div className="flex flex-wrap gap-2 pt-1 border-t" style={{ borderColor: 'var(--color-hairline)' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="send-email-button"
                    onClick={() => setSendEmailOpen(true)}
                  >
                    Send email
                  </Button>
                </div>
              </Card>

              <OverridesCard userId={id} overrides={subData.overrides} />
              <EventsCard events={subData.events} />
              <EmailLogCard userId={user.id} />
              <SuppressionPanel userId={user.id} userEmail={user.email} />
            </>
          ) : (
            <div className="text-ink-soft text-sm">Failed to load subscription data.</div>
          )}
        </div>

        {/* Right column — Profile card */}
        <div className="lg:sticky lg:top-6">
          <ProfileCard
            user={{
              id: user.id,
              email: user.email,
              displayName: user.displayName,
              role: user.role,
              locale: user.locale ?? null,
            }}
            canDelete={me.id !== user.id}
            onEdit={() => setEditProfileOpen(true)}
            onDelete={() => setDeleteDialogOpen(true)}
          />
        </div>
      </div>

      {/* Dialogs */}
      {dialogOpen === 'grant-plus' && (
        <GrantPlusDialog userId={id} onClose={() => setDialogOpen(null)} />
      )}
      {dialogOpen === 'extend-trial' && (
        <ExtendTrialDialog userId={id} onClose={() => setDialogOpen(null)} />
      )}
      {dialogOpen === 'quota-limit' && (
        <SetQuotaLimitDialog userId={id} onClose={() => setDialogOpen(null)} />
      )}
      {dialogOpen === 'reset-usage' && (
        <ResetUsageDialog userId={id} onClose={() => setDialogOpen(null)} />
      )}
      {editProfileOpen && user && (
        <EditProfileDialog
          user={{
            id: user.id,
            displayName: user.displayName,
            role: user.role,
            locale: user.locale ?? null,
          }}
          onClose={() => setEditProfileOpen(false)}
        />
      )}
      {deleteDialogOpen && user && (
        <DeleteUserDialog
          userId={user.id}
          displayName={user.displayName}
          onClose={() => setDeleteDialogOpen(false)}
          onDeleted={() => navigate({ to: '/users' })}
        />
      )}
      <SendEmailDialog
        userId={user.id}
        open={sendEmailOpen}
        onClose={() => setSendEmailOpen(false)}
      />
    </div>
  );
}

// ── DeviceSessionsCard ────────────────────────────────────────────────────────

function formatRelativeTime(seenAtMs: number): string {
  const diffMs = Date.now() - seenAtMs;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  return `${diffMonths} months ago`;
}

function DeviceSessionsCard({ sessions }: { sessions: AdminDeviceSessionRow[] }) {
  if (sessions.length === 0) {
    return (
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-2" data-testid="user-detail-sessions-card">
        <h2 className="font-serif text-xl">Device sessions</h2>
        <p className="text-ink-faint text-sm">No device sessions recorded.</p>
      </Card>
    );
  }
  return (
    <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-3" data-testid="user-detail-sessions-card">
      <h2 className="font-serif text-xl">Device sessions</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-faint border-b" style={{ borderColor: 'var(--color-hairline)' }}>
            <th className="pb-2 pr-4">Platform</th>
            <th className="pb-2 pr-4">Version</th>
            <th className="pb-2 pr-4">Runtime</th>
            <th className="pb-2 pr-4">OTA</th>
            <th className="pb-2">Last seen</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => {
            const isNative = s.platform === 'ios' || s.platform === 'android';
            return (
              <tr
                key={s.platform}
                data-testid={`user-detail-session-row-${s.platform}`}
                className="border-b last:border-b-0"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <td className="py-2 pr-4 font-medium capitalize">{s.platform}</td>
                <td className="py-2 pr-4 font-mono text-xs" data-testid={`user-detail-session-app-version-${s.platform}`}>
                  {s.app_version}{isNative && s.build_number ? ` (build ${s.build_number})` : ''}
                </td>
                {isNative ? (
                  <td className="py-2 pr-4 font-mono text-xs text-ink-soft" data-testid={`user-detail-session-runtime-${s.platform}`}>
                    {s.runtime_version ?? '—'}
                  </td>
                ) : (
                  <td className="py-2 pr-4 text-ink-faint text-xs">—</td>
                )}
                {isNative ? (
                  <td className="py-2 pr-4 font-mono text-xs text-ink-soft" data-testid={`user-detail-session-ota-${s.platform}`}>
                    {s.ota_update_id ?? <em>embedded</em>}
                  </td>
                ) : (
                  <td className="py-2 pr-4 text-ink-faint text-xs">—</td>
                )}
                <td className="py-2 text-ink-soft text-xs" data-testid={`user-detail-session-lastseen-${s.platform}`}>
                  {formatRelativeTime(s.seen_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
